import { TodoList2 } from '@griply/assets';
import { getGoalLifeArea, getNotAllowedParentGoalIds } from '@griply/utils';
import { useGoals, useSortedGoals, useUpdateGoal } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search } from 'shared/assets/icons';
import { GoalTreeItemIds } from 'shared/components/connected/goal-context-menu/types';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuNoResults,
} from 'shared/components/ui/popup-menu';
import { useFocusChildrenWithArrows } from 'shared/hooks/use-focus-children-with-arrows';
import { ID } from 'shared/types/id';

import * as Styled from './goal-tree-context-menu.style';
import { MoveGoalItem } from './move-goal-item';
import { useFilteredGoalTreeItems } from './use-filtered-goal-tree-items';

export type GoalTreeContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'children' | 'ref'
> & {
  /** The goal being moved. Omit when reusing the menu as a pure picker via `onSelect`. */
  id?: ID;
  onClose: () => void;
  /**
   * When provided, the picker calls `onSelect` instead of mutating the goal
   * directly. Callers receive the resolved `parentIds` / `lifeAreaId` and own
   * the persistence (e.g. holding the value in form state until form Save).
   */
  onSelect?: (parentIds: ID[], lifeAreaId: ID | null) => void;
  /**
   * The item already picked — the container a section lives in, the goal or
   * life area a calendar sync targets. Highlights it, opens its branch, and
   * disables re-picking it. Only that item is disabled (descendants stay valid
   * targets, unlike goal-move cycle prevention).
   */
  currentSelection?: { goalId?: ID; lifeAreaId?: ID };
  /**
   * Hides the "No life area" bucket. Used by link-pickers (e.g. the calendar
   * slot menu) where "no life area" is not a selectable target.
   */
  hideNoLifeArea?: boolean;
  // Opt-in pinned "all tasks" shortcut — only calendar-sync uses it; label kept caller-owned so this
  // shared menu stays free of feature copy.
  allTasksLabel?: string;
  isAllTasksSelected?: boolean;
  onSelectAllTasks?: () => void;
};

export const GoalTreeContextMenu: React.FC<GoalTreeContextMenuProps> = ({
  id,
  onClose,
  onSelect,
  currentSelection,
  hideNoLifeArea,
  allTasksLabel,
  isAllTasksSelected,
  onSelectAllTasks,
  position,
  location,
  referenceElement,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // need raw goals for recursive sub-goal lookups if filteredGoals contains items from useSortedGoals which might be filtered
  const allGoals = useGoals();
  // pass options to get all goals, as default is activeOnly
  const goals = useSortedGoals();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });
  const goal = useMemo(() => goals.find((goal) => goal.id === id), [goals, id]);

  const [searchValue, setSearchValue] = useState('');
  const [expandedItems, setExpandedItems] = useState<Set<ID>>(() => {
    // Open the branch the user needs to see: the goal being moved, else the already-picked target.
    const focusGoalId = goal?.id ?? currentSelection?.goalId;
    if (!focusGoalId) {
      return new Set<ID>(
        currentSelection?.lifeAreaId ? [currentSelection.lifeAreaId] : [],
      );
    }

    const focusGoal = allGoals.find((g) => g.id === focusGoalId);
    const initial = new Set<ID>([focusGoalId, ...(focusGoal?.parentIds ?? [])]);

    const lifeAreaId = focusGoal
      ? getGoalLifeArea(focusGoal, allGoals)
      : undefined;
    initial.add(lifeAreaId ?? GoalTreeItemIds.NoLifeArea);

    return initial;
  });

  const { submit: updateGoal } = useUpdateGoal();

  useFocusChildrenWithArrows(containerRef);

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchValue(e.target.value);

  const parentId = goal?.parentIds?.[goal.parentIds.length - 1];
  const currentLifeAreaId = goal?.lifeAreaId;
  const hasCurrentSelection = !!currentSelection;

  const onGoal = (parentIds: ID[], lifeAreaId?: ID | null) => {
    if (onSelect) {
      onSelect(parentIds, lifeAreaId ?? null);
      onClose();
      return;
    }
    if (!id) {
      onClose();
      return;
    }
    // If we move to a parent that is archived, we should also archive this goal
    const newParentId =
      parentIds.length > 0 ? parentIds[parentIds.length - 1] : undefined;
    const newParent = allGoals.find((g) => g.id === newParentId);
    const shouldArchive = !!newParent?.archivedAt && !goal?.archivedAt;

    updateGoal({
      id,
      parentIds,
      lifeAreaId,
      ...(shouldArchive ? { archivedAt: new Date() } : {}),
    });
    onClose();
  };

  const toggleCollapse = (e: React.MouseEvent, id: ID) => {
    e.preventDefault();
    e.stopPropagation();
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Create a map of goal indices for sorting
  const goalIndexMap = useMemo(() => {
    const map = new Map<ID, number>();
    goals.forEach((g, index) => map.set(g.id, index));
    return map;
  }, [goals]);

  const filteredItems = useFilteredGoalTreeItems({
    allGoals,
    lifeAreas,
    searchValue,
    expandedItems,
    goalIndexMap,
  });

  const notAllowedGoalIds = useMemo(
    () =>
      id
        ? getNotAllowedParentGoalIds(id, {
            goals: allGoals,
            currentParentId: parentId,
          })
        : new Set<ID>(),
    [id, parentId, allGoals],
  );

  const visibleItems = useMemo(
    () =>
      hideNoLifeArea
        ? filteredItems.filter(
            (item) => item.type !== GoalTreeItemIds.NoLifeArea,
          )
        : filteredItems,
    [filteredItems, hideNoLifeArea],
  );

  return (
    <Styled.Menu
      ref={containerRef}
      location={location}
      position={position}
      referenceElement={referenceElement}
      onOutside={onClose}
      initialFocus={inputRef}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuInput
            ref={inputRef}
            startIcon={Search}
            value={searchValue}
            placeholder={t('general.labels.search')}
            onChange={onChangeInput}
          />
        </PopupMenuListItem>
      </PopupMenuList>

      {!!onSelectAllTasks && !searchValue && (
        <PopupMenuList hasBottomBorder>
          <PopupMenuListItem>
            <PopupMenuButton
              start={<Icon icon={TodoList2} />}
              onClick={onSelectAllTasks}
              isSelected={isAllTasksSelected}
              end={isAllTasksSelected ? <Icon icon={Check} /> : undefined}
              tabIndex={0}
            >
              {allTasksLabel}
            </PopupMenuButton>
          </PopupMenuListItem>
        </PopupMenuList>
      )}

      <PopupMenuList>
        {!visibleItems.length && !!searchValue && (
          <PopupMenuNoResults>
            {t('general.labels.no-results')}
          </PopupMenuNoResults>
        )}
        {visibleItems.map((item) => {
          if (item.type === GoalTreeItemIds.NoLifeArea) {
            // Never a caller-supplied current selection — neither a section nor a sync can live here.
            const isSelected =
              !hasCurrentSelection &&
              !parentId &&
              !currentLifeAreaId &&
              !searchValue;
            const isDisabled = isSelected;
            const isExpanded = expandedItems.has(GoalTreeItemIds.NoLifeArea);

            return (
              <MoveGoalItem
                key={GoalTreeItemIds.NoLifeArea}
                item={item}
                isSelected={isSelected}
                isDisabled={isDisabled}
                isExpanded={isExpanded}
                onGoal={onGoal}
                toggleCollapse={toggleCollapse}
                searchValue={searchValue}
                isParent={false}
              />
            );
          }

          if (item.type === GoalTreeItemIds.LifeArea) {
            const isSelected = hasCurrentSelection
              ? currentSelection.lifeAreaId === item.id && !searchValue
              : !parentId && currentLifeAreaId === item.id && !searchValue;

            // Disable the container the section currently lives in (or, for a
            // goal move, the goal's current life area).
            const isDisabled = isSelected;
            const isExpanded = expandedItems.has(item.id);

            return (
              <MoveGoalItem
                key={`life-area-${item.id}`}
                item={item}
                isSelected={isSelected}
                isDisabled={isDisabled}
                isExpanded={isExpanded}
                onGoal={onGoal}
                toggleCollapse={toggleCollapse}
                searchValue={searchValue}
                isParent={false}
              />
            );
          }

          // Caller-supplied selection: highlight it and disable only it —
          // descendants stay valid targets.
          // Goal move: highlight the goal's parent and disable it + descendants.
          const isCurrentGoal =
            hasCurrentSelection && currentSelection.goalId === item.id;
          const isExpanded = expandedItems.has(item.id);
          const isParent = hasCurrentSelection
            ? isCurrentGoal
            : item.id === parentId;
          const isDisabled = hasCurrentSelection
            ? isCurrentGoal
            : notAllowedGoalIds.has(item.id);

          return (
            <MoveGoalItem
              key={`goal-${item.id}`}
              item={item}
              isSelected={isParent}
              isDisabled={isDisabled}
              isExpanded={isExpanded}
              onGoal={onGoal}
              toggleCollapse={toggleCollapse}
              searchValue={searchValue}
              isParent={isParent}
            />
          );
        })}
      </PopupMenuList>
    </Styled.Menu>
  );
};
